import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

export interface JobArtifact {
  id: string;
  job_id?: string;
  artifact_type: string;
  version: number;
  status: 'draft' | 'published';
  name: string;
  content_markdown: string;
  used_content_ids: string[];
  published_at?: string;
  submitted_at?: string;
  stylesheet_id?: string;
  metadata?: Record<string, any>;
  pdf_storage_path?: string | null;
  pdf_generated_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateArtifactRequest {
  job_id?: string;
  artifact_type: string;
  name: string;
  content_ids: string[];
  stylesheet_id?: string;
  metadata?: Record<string, any>;
}

export interface CompileArtifactRequest {
  job_id?: string;
  artifact_type: string;
  name: string;
  content_markdown: string;
  content_ids?: string[];
  stylesheet_id?: string;
  metadata?: Record<string, any>;
}

export interface UpdateArtifactRequest {
  name?: string;
  content_markdown?: string;
  stylesheet_id?: string;
  metadata?: Record<string, any>;
}

// ==================== QUERIES ====================

/**
 * Fetch all artifacts with optional filtering
 */
export const useArtifacts = (jobId?: string, type?: string, status?: 'draft' | 'published') => {
  return useQuery({
    queryKey: ['artifacts', jobId, type, status],
    queryFn: async () => {
      let url = `${API_BASE}/artifacts`;
      const params = new URLSearchParams();
      if (jobId) params.append('jobId', jobId);
      if (type) params.append('type', type);
      if (status) params.append('status', status);

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const headers = await getAuthHeaders();
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Failed to fetch artifacts');

      const response = await res.json();
      return response.data as JobArtifact[];
    },
  });
};

/**
 * Fetch artifacts for a specific job
 */
export const useArtifactsByJob = (jobId: string | undefined, options?: { polling?: boolean }) => {
  console.log('[useArtifactsByJob] Hook called with polling:', options?.polling, 'jobId:', jobId);

  return useQuery({
    queryKey: ['artifacts', 'byJob', jobId],
    queryFn: async () => {
      let url = `${API_BASE}/artifacts`;
      if (jobId) {
        url += `?jobId=${encodeURIComponent(jobId)}`;
      }

      console.log('[useArtifactsByJob] queryFn executing. Polling:', options?.polling);
      if (options?.polling) {
        console.log('[useArtifactsByJob] Polling fetch for job:', jobId);
      }

      const headers = await getAuthHeaders();
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Failed to fetch artifacts');

      const response = await res.json();
      const artifacts = response.data as JobArtifact[];

      if (options?.polling) {
        console.log('[useArtifactsByJob] Poll result - found', artifacts.length, 'artifacts');
        const placeholders = artifacts.filter(a => a.content_markdown?.includes('⏳ Generating with AI'));
        const completed = artifacts.filter(a => !a.content_markdown?.includes('⏳ Generating with AI'));
        console.log('[useArtifactsByJob] Breakdown - placeholders:', placeholders.length, 'completed:', completed.length);

        // Log detailed info about each artifact during polling
        artifacts.forEach((a, idx) => {
          const content = a.content_markdown || '';
          const hasPlaceholderText = content.includes('⏳ Generating with AI');
          const hasPlaceholderEmoji = content.includes('⏳');
          console.log(`[useArtifactsByJob] Artifact ${idx}:`, {
            id: a.id,
            name: a.name,
            contentLength: content.length,
            updated_at: a.updated_at,
            isPlaceholder: hasPlaceholderText,
            hasEmojiAlone: hasPlaceholderEmoji,
            // Show first 150 chars of content
            contentPreview: content.substring(0, 150),
            // Also log the raw first 50 chars as JSON to see exact chars
            rawStart: JSON.stringify(content.substring(0, 50)),
          });
        });
      }

      return artifacts;
    },
    enabled: !!jobId,
    // Enable polling when isGenerating is true
    refetchInterval: options?.polling ? 2000 : false,
  });
};

/**
 * Fetch a specific artifact by ID
 */
export const useArtifact = (id: string | undefined) => {
  return useQuery({
    queryKey: ['artifacts', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch artifact');

      const response = await res.json();
      return response.data as JobArtifact;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new artifact
 */
export const useCreateArtifact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateArtifactRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create artifact');
      }
      const response = await res.json();
      return response.data as JobArtifact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      if (data.job_id) {
        queryClient.invalidateQueries({ queryKey: ['artifacts', 'byJob', data.job_id] });
      }
    },
  });
};

/**
 * Compile and create a new artifact with markdown content
 */
export const useCompileArtifact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CompileArtifactRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/compile`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to compile artifact');
      }
      const response = await res.json();
      return response.data as JobArtifact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      if (data.job_id) {
        queryClient.invalidateQueries({ queryKey: ['artifacts', 'byJob', data.job_id] });
      }
    },
  });
};

/**
 * Update an existing artifact
 */
export const useUpdateArtifact = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateArtifactRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update artifact');
      }
      const response = await res.json();
      return response.data as JobArtifact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['artifacts', id] });
      if (data.job_id) {
        queryClient.invalidateQueries({ queryKey: ['artifacts', 'byJob', data.job_id] });
      }
    },
  });
};

/**
 * Publish or unpublish an artifact
 */
export const usePublishArtifact = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/${id}/publish`, {
        method: 'PUT',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to publish artifact');
      }
      const response = await res.json();
      return response.data as JobArtifact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['artifacts', id] });
      if (data.job_id) {
        queryClient.invalidateQueries({ queryKey: ['artifacts', 'byJob', data.job_id] });
      }
    },
  });
};

/**
 * Delete an artifact
 */
export const useDeleteArtifact = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/artifacts/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete artifact');
      }
      return res.json() as Promise<{ success: boolean }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    },
  });
};
